<template>
  <b-card aria-label="Aperçu des factures multiples">
    <div
      v-if="isLoading"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Chargement"
      />
    </div>

    <div v-else>
      <h4 class="mb-3">
        Aperçu des Factures ({{ suppliers.length }} fournisseurs)
      </h4>

      <!-- Tabs for each supplier -->
      <b-tabs
        v-model="activeTab"
        content-class="mt-3"
      >
        <b-tab
          v-for="supplier in suppliers"
          :key="supplier.id"
          :title="supplier.username"
        >
          <!-- Invoice Preview for this supplier -->
          <div v-if="invoiceData[supplier.id]">
            <b-card class="mb-3">
              <h5>{{ supplier.username }}</h5>
              <p class="text-muted">
                {{ invoiceData[supplier.id].nb_orders }} commande(s) • {{ formatCurrency(invoiceData[supplier.id].total_ttc) }}
              </p>

              <!-- Orders Table -->
              <h6 class="mt-3">
                Colis livrés ({{ invoiceData[supplier.id].delivred.length }})
              </h6>
              <b-table-simple
                v-if="invoiceData[supplier.id].delivred.length > 0"
                responsive
                class="mb-3"
              >
                <b-thead>
                  <b-tr>
                    <b-th>Code</b-th>
                    <b-th>Destinataire</b-th>
                    <b-th>Prix TTC</b-th>
                    <b-th>Frais Livraison</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr
                    v-for="order in invoiceData[supplier.id].delivred"
                    :key="order.id"
                  >
                    <b-td>{{ order.id }}</b-td>
                    <b-td>{{ order.name }}</b-td>
                    <b-td>{{ formatCurrency(order.price_ttc) }}</b-td>
                    <b-td>{{ formatCurrency(order.price_delivery) }}</b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>

              <h6 class="mt-3">
                Colis retours ({{ invoiceData[supplier.id].return.length }})
              </h6>
              <b-table-simple
                v-if="invoiceData[supplier.id].return.length > 0"
                responsive
              >
                <b-thead>
                  <b-tr>
                    <b-th>Code</b-th>
                    <b-th>Destinataire</b-th>
                    <b-th>Frais Retour</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr
                    v-for="order in invoiceData[supplier.id].return"
                    :key="order.id"
                  >
                    <b-td>{{ order.id }}</b-td>
                    <b-td>{{ order.name }}</b-td>
                    <b-td>{{ formatCurrency(order.price_return) }}</b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>

              <!-- Totals -->
              <b-card
                class="mt-3"
                bg-variant="light"
              >
                <b-row>
                  <b-col>Total TTC:</b-col>
                  <b-col class="text-right">
                    <strong>{{ formatCurrency(invoiceData[supplier.id].total_ttc) }}</strong>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>Frais de livraison:</b-col>
                  <b-col class="text-right">
                    {{ formatCurrency(invoiceData[supplier.id].total_delivery) }}
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>Frais de retour:</b-col>
                  <b-col class="text-right">
                    {{ formatCurrency(invoiceData[supplier.id].total_return) }}
                  </b-col>
                </b-row>
                <b-row style="background-color: #fef3c7;">
                  <b-col><strong>Frais:</strong></b-col>
                  <b-col class="text-right">
                    <strong>{{ formatCurrency(invoiceData[supplier.id].frais_societe_livraison) }}</strong>
                  </b-col>
                </b-row>
                <hr>
                <b-row>
                  <b-col>
                    <strong>Net à payer:</strong>
                  </b-col>
                  <b-col class="text-right">
                    <strong class="text-success">{{ formatCurrency(invoiceData[supplier.id].net_a_payer) }}</strong>
                  </b-col>
                </b-row>
              </b-card>
            </b-card>
          </div>

          <!-- No orders message -->
          <b-alert
            v-else-if="errors[supplier.id]"
            show
            variant="warning"
          >
            {{ errors[supplier.id] }}
          </b-alert>
        </b-tab>
      </b-tabs>

      <!-- Actions -->
      <div class="d-flex justify-content-between mt-4">
        <b-button
          variant="outline-secondary"
          @click="goBack"
        >
          <feather-icon
            icon="ArrowLeftIcon"
            class="mr-1"
          />
          Retour
        </b-button>
        <b-button
          variant="success"
          :disabled="isConfirming || Object.keys(invoiceData).length === 0"
          @click="confirmAll"
        >
          <b-spinner
            v-if="isConfirming"
            small
            class="mr-1"
          />
          <feather-icon
            v-else
            icon="CheckIcon"
            class="mr-1"
          />
          Confirmer toutes les factures
        </b-button>
      </div>
    </div>
  </b-card>
</template>

<script>
import {
  BCard, BAlert, BButton, BSpinner, BTabs, BTab,
  BTableSimple, BThead, BTbody, BTr, BTh, BTd, BRow, BCol,
} from 'bootstrap-vue'
import axios from 'axios'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'BatchInvoicePreview',
  components: {
    BCard,
    BAlert,
    BButton,
    BSpinner,
    BTabs,
    BTab,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    BRow,
    BCol,
  },
  data() {
    return {
      suppliers: [],
      invoiceData: {},
      errors: {},
      activeTab: 0,
      isLoading: false,
      isConfirming: false,
      fraisSocieteLivraison: 0,
      blockInvoicesWithoutDeliveredOrders: false,
    }
  },
  async created() {
    await storeAuth.dispatch('fetchCompanyInfo')
    this.updateBlockConfig()
    await this.loadSupplierData()
  },
  methods: {
    updateBlockConfig() {
      const { company } = storeAuth.state
      this.blockInvoicesWithoutDeliveredOrders = company && company.allow_invoices_without_delivered_orders === false
    },

    async loadSupplierData() {
      const supplierIds = JSON.parse(sessionStorage.getItem('batchSupplierIds') || '[]')
      const endDate = sessionStorage.getItem('invoiceEndDate') || null

      if (!supplierIds || supplierIds.length === 0) {
        this.$router.push({ name: 'invoice-add' })
        return
      }

      this.isLoading = true

      try {
        // Fetch preview data for each supplier in parallel
        const promises = supplierIds.map(async supplierId => {
          try {
            const previewParams = { supplier: supplierId }
            if (endDate) {
              previewParams.delivered_at__lte = endDate
            }
            const { data } = await axios.get('/api/invoices/preview/', {
              params: previewParams,
            })

            if (data && data.length > 0) {
              const { supplier } = data[0]

              const delivred = data.filter(o => o.status === 4)
              const returnOrders = data.filter(o => o.status === 7)

              if (this.blockInvoicesWithoutDeliveredOrders && delivred.length === 0) {
                this.errors[supplierId] = `${supplier.username} : aucun colis livré. Impossible de générer une facture sans colis livré.`
                return
              }

              this.suppliers.push(supplier)

              const totalTtc = delivred.reduce((sum, o) => sum + parseFloat(o.price_ttc || 0), 0)
              const totalDelivery = delivred.reduce((sum, o) => sum + parseFloat(o.price_delivery || 0), 0)
              const totalReturn = returnOrders.reduce((sum, o) => sum + parseFloat(o.price_return || 0), 0)

              const total = totalTtc - (totalDelivery + totalReturn)

              // Calculate company fee dynamically based on new configuration
              let supplierFee = 0
              try {
                const feeRes = await axios.post('/api/core/company-fee/preview/', {
                  supplier_id: supplierId,
                  invoice_total: total,
                })
                supplierFee = feeRes.data.fee_amount || 0
              } catch (e) {
                // eslint-disable-next-line no-console
                console.error(`Error fetching fee for supplier ${supplierId}:`, e)
              }

              const netAPayer = total - supplierFee

              this.invoiceData[supplierId] = {
                delivred,
                return: returnOrders,
                nb_orders: delivred.length + returnOrders.length,
                total_ttc: totalTtc,
                total_delivery: totalDelivery,
                total_return: totalReturn,
                total,
                frais_societe_livraison: supplierFee,
                net_a_payer: netAPayer,
              }
            } else {
              this.errors[supplierId] = 'Aucune commande éligible trouvée'
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(`Error fetching data for supplier ${supplierId}:`, error)
            this.errors[supplierId] = 'Erreur lors du chargement des données'
          }
        })

        await Promise.all(promises)
      } finally {
        this.isLoading = false
      }
    },

    async confirmAll() {
      this.isConfirming = true

      try {
        const supplierIds = Object.keys(this.invoiceData).map(id => parseInt(id, 10))
        const endDate = sessionStorage.getItem('invoiceEndDate') || null
        const batchPayload = { supplier_ids: supplierIds }
        if (endDate) {
          batchPayload.end_date = endDate
        }
        const { data } = await axios.post('/api/invoices/create-batch-invoices/', batchPayload)

        // Store results and navigate to results page
        sessionStorage.setItem('batchInvoiceResults', JSON.stringify(data))
        sessionStorage.removeItem('batchSupplierIds')

        this.$router.push({ name: 'batch-invoices' })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating batch invoices:', error)
        this.showToast('danger', 'top-center', error.response?.data?.error || 'Erreur lors de la création des factures')
      } finally {
        this.isConfirming = false
      }
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('fr-TN', {
        style: 'currency',
        currency: 'TND',
      }).format(amount || 0)
    },

    goBack() {
      sessionStorage.removeItem('batchSupplierIds')
      this.$router.push({ name: 'invoice-add' })
    },

    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.text-right {
  text-align: right;
}
</style>
